import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  activatePolicyVersion,
  explainPolicy,
  listPolicyVersions,
  seedDefaultPolicy,
} from "@/lib/api";
import type {
  ConfidenceBand,
  ExplainResponse,
  PolicyVersion,
  ThesisStatus,
} from "@/lib/api/types";
import { fmtScore } from "@/lib/format";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Label, NumberInput, Select } from "@/components/ui/Input";
import { DataTable } from "@/components/ui/Table";
import {
  ConfidenceBadge,
  StanceBadge,
  ThesisBadge,
} from "@/components/ui/Badges";
import { LoadingState, EmptyState, ErrorState } from "@/components/ui/States";
import { Pill } from "@/components/ui/Pill";
import { RuleBuilderPanel } from "@/components/policy/RuleBuilder";

export function PolicyPage() {
  const versions = useQuery({
    queryKey: ["policy", "versions"],
    queryFn: listPolicyVersions,
  });
  const queryClient = useQueryClient();
  const seed = useMutation({
    mutationFn: () => seedDefaultPolicy(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["policy"] }),
  });
  const activate = useMutation({
    mutationFn: (id: number) => activatePolicyVersion(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["policy"] }),
  });

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const selected =
    versions.data?.find((v) => v.id === selectedId) ??
    versions.data?.find((v) => v.is_active) ??
    versions.data?.[0] ??
    null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold tracking-tight">Policy engine</h1>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => seed.mutate()}
            loading={seed.isPending}
          >
            {seed.isPending ? "Seeding…" : "Seed default policy"}
          </Button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-1"
          title="Versions"
          description="One active version per name."
        >
          {versions.isLoading && <LoadingState />}
          {versions.data && versions.data.length === 0 && (
            <EmptyState
              title="No policy versions"
              description="Click 'Seed default policy' to install the spec's canonical policy."
            />
          )}
          {versions.data && versions.data.length > 0 && (
            <ul className="space-y-1 text-sm">
              {versions.data.map((v) => (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(v.id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left transition ${
                      selected?.id === v.id
                        ? "border-accent bg-accent-subtle text-accent-strong"
                        : "border-transparent hover:bg-surface-subtle"
                    }`}
                  >
                    <span className="space-y-0.5">
                      <span className="block font-medium">{v.name} <span className="text-foreground-muted">v{v.version}</span></span>
                      <span className="block text-2xs text-foreground-muted">
                        {v.rules.length} rule{v.rules.length === 1 ? "" : "s"}
                      </span>
                    </span>
                    {v.is_active && <Pill tone="accent">active</Pill>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          className="lg:col-span-2"
          title={selected ? `${selected.name} v${selected.version}` : "Rules"}
          description={selected?.description ?? "Select a version to view its rules."}
          action={
            selected && !selected.is_active ? (
              <Button
                size="sm"
                variant="primary"
                loading={activate.isPending}
                onClick={() => activate.mutate(selected.id)}
              >
                Activate
              </Button>
            ) : null
          }
        >
          {selected && <RuleTable version={selected} />}
        </Panel>
      </div>

      <RuleBuilderPanel source={selected} />

      <ExplainPanel activePolicyId={selected?.id} />
    </div>
  );
}

function RuleTable({ version }: { version: PolicyVersion }) {
  if (version.rules.length === 0) {
    return <EmptyState title="No rules in this version" />;
  }
  return (
    <DataTable
      dense
      rows={[...version.rules].sort((a, b) => a.priority - b.priority)}
      rowKey={(r) => r.id}
      columns={[
        {
          key: "priority",
          header: "Pri",
          cell: (r) => r.priority,
          align: "right",
        },
        { key: "name", header: "Name", cell: (r) => r.name },
        {
          key: "expression",
          header: "Expression",
          cell: (r) => (
            <code className="text-2xs text-foreground-secondary">{r.expression}</code>
          ),
        },
        {
          key: "stance",
          header: "Stance",
          cell: (r) => <StanceBadge label={r.output_stance} />,
        },
      ]}
    />
  );
}

function ExplainPanel({ activePolicyId }: { activePolicyId: number | undefined }) {
  const [composite, setComposite] = useState(75);
  const [risk, setRisk] = useState(40);
  const [confidence, setConfidence] = useState<ConfidenceBand>("high");
  const [thesis, setThesis] = useState<ThesisStatus>("stable");
  const [coverage, setCoverage] = useState<number | "">(0.9);
  const [result, setResult] = useState<ExplainResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const explain = useMutation({
    mutationFn: () =>
      explainPolicy({
        policy_version_id: activePolicyId ?? null,
        inputs: {
          composite,
          risk_score: risk,
          confidence_band: confidence,
          thesis_status: thesis,
          coverage: coverage === "" ? null : Number(coverage),
        },
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err: unknown) => {
      setResult(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  return (
    <Panel
      title="Explain"
      description="Dry-run the active policy against hypothetical inputs."
    >
      <div className="grid gap-3 lg:grid-cols-[1fr,1fr]">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Composite</Label>
            <NumberInput
              min={0}
              max={100}
              value={composite}
              onChange={(e) => setComposite(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Risk score</Label>
            <NumberInput
              min={0}
              max={100}
              value={risk}
              onChange={(e) => setRisk(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1">
            <Label>Confidence band</Label>
            <Select
              value={confidence}
              onChange={(e) => setConfidence(e.target.value as ConfidenceBand)}
            >
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
              <option value="insufficient">insufficient</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Thesis</Label>
            <Select
              value={thesis}
              onChange={(e) => setThesis(e.target.value as ThesisStatus)}
            >
              <option value="improving">improving</option>
              <option value="stable">stable</option>
              <option value="deteriorating">deteriorating</option>
              <option value="unknown">unknown</option>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Coverage</Label>
            <NumberInput
              min={0}
              max={1}
              step={0.05}
              value={coverage}
              onChange={(e) =>
                setCoverage(e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              variant="primary"
              size="sm"
              loading={explain.isPending}
              onClick={() => explain.mutate()}
              disabled={!activePolicyId}
            >
              Run explain
            </Button>
          </div>
        </div>

        <div className="space-y-2 rounded-md border border-border-subtle bg-surface-base p-3">
          {error && <ErrorState detail={error} />}
          {!result && !error && (
            <p className="text-xs text-foreground-muted">
              Adjust inputs and run explain to see which rule fires.
            </p>
          )}
          {result && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="accent">stance</Pill>
                <span className="text-foreground-primary">{result.stance_label}</span>
                <ConfidenceBadge band={confidence} />
                <ThesisBadge status={thesis} />
              </div>
              {result.short_circuit_reason ? (
                <p className="text-xs text-warning">
                  Short-circuit: {result.short_circuit_reason}
                </p>
              ) : (
                <p className="text-xs text-foreground-secondary">{result.explanation}</p>
              )}
              {result.trace.length > 0 && (
                <ul className="space-y-1 text-2xs">
                  {result.trace.map((t) => (
                    <li
                      key={t.rule_id}
                      className={`rounded border px-2 py-1 ${
                        t.matched
                          ? "border-success/40 bg-success/10 text-success"
                          : "border-border-subtle bg-surface-subtle text-foreground-secondary"
                      }`}
                    >
                      <span className="font-mono">#{t.rule_id}</span>{" "}
                      <span className="font-medium">{t.name}</span>{" "}
                      <span className="text-foreground-muted">→ {t.matched ? "matched" : "skipped"}</span>
                      <div className="font-mono text-2xs text-foreground-muted">{t.expression}</div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      <p className="pt-3 text-2xs text-foreground-muted">
        composite={fmtScore(composite)} · risk={fmtScore(risk)} · confidence={confidence} · thesis={thesis}
      </p>
    </Panel>
  );
}
